import {
  Box,
  classSet,
  InputIcon,
  InputIconProps,
  Portal,
  useRect
} from '@platform/ui-library';
import {
  FC,
  KeyboardEvent,
  SyntheticEvent,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  FormControlWithOptionProps,
  FormInputProps,
  Option
} from '@app/types';
import { ControlWithFormField, FormFieldControlProps } from '../form-field';
import styles from './autocomplete.module.scss';

type AutocompleteBoldedOption<TOption extends Option<unknown>> = {
  boldSubstring: string;
  option: TOption | null;
  firstSubstring: string;
  lastSubstring: string;
};

export type AutocompleteControlProps<TValue, TOption extends Option<TValue>> = (
  FormControlWithOptionProps<TValue | null, TOption | null>
  & FormInputProps
  & Pick<InputIconProps, 'autoFocus' | 'icon'>
  & {
    /** Whether the input that the user can type in is disabled. */
    disabled?: boolean;
    /**
     * Label for the empty option at the top of the dropdown @default ''.
     *
     * @note If {''} (default), no empty option is shown, but search is cleared on blur.
     * @note If {null}, autocomplete does not clear on blur.
     */
    emptyLabel?: null | string;
    /** Gets options that should be displayed for the current @see searchValue. */
    getOptions: (searchValue: string) => (
      TOption[]
      | Promise<TOption[]>
    );
    /** Replace the default labels. */
    labels?: Partial<{
      /** Displayed while waiting for @see getOptions to resolve. */
      isLoading: string;
      /** Displayed when @see getOptions does not return options. */
      notFound: string;
      /** Displayed at the top of the dropdown options. */
      title: string;
    }>;
    /** Minimum length @see searchValue has to be before @see getOptions is called @default 0. */
    minSearchLength?: number;
    /** Whether the input that the user can type in is required. */
    required?: boolean;
    style?: {
      dropdown?: {
        width?: string;
      };
    };
  } & (
    // Parent component controls the autocomplete's search.
    {
      initSearchValue?: never;
      /** Called when the user types in the search input. */
      onSearchValueChange: (searchValue: string) => void;
      /** Value displayed in the search input. */
      searchValue: string;
    }
    // Autocomplete controls its own search value.
    | {
      /** Search value to start with. */
      initSearchValue?: string | null;
      onSearchValueChange?: never;
      searchValue?: never;
    }
  )
);

export function AutocompleteControl<TValue, TOption extends Option<TValue> = Option<TValue>>({
  disabled,
  emptyLabel = '',
  getOptions,
  initSearchValue,
  labels,
  minSearchLength = 0,
  onBlur,
  onFocus,
  onSearchValueChange,
  onChange,
  searchValue: propsSearchValue,
  style,
  value,
  ...inputProps
}: AutocompleteControlProps<TValue, TOption>): ReturnType<FC> {
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<TOption[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [stateSearchValue, setStateSearchValue] = useState(
    initSearchValue ?? emptyLabel ?? ''
  );
  const searchValue = (propsSearchValue ?? stateSearchValue);
  const setSearchValue = onSearchValueChange ?? setStateSearchValue;
  const [containerRef, bbox, updateContainerBbox] = useRect();
  const getOptionsRef = useRef(getOptions);
  getOptionsRef.current = getOptions;

  useEffect(() => {
    if (
      isFocused
      && (searchValue.length ?? 0) >= minSearchLength
    ) {
      let cancelled = false;
      (async () => {
        setIsLoading(true);
        setIsOpen(true);
        const result = await getOptionsRef.current(searchValue);
        if (!cancelled) {
          setOptions(result);
          setIsLoading(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    } else if (isOpen) {
      setIsOpen(false);
    }
  }, [
    isFocused,
    minSearchLength,
    searchValue
  ]);

  useEffect(() => {
    if (isOpen) {
      updateContainerBbox();
    }
  }, [isOpen, disabled]);

  useEffect(() => {
    if (emptyLabel != null && !value) {
      if (!isFocused) {
        setSearchValue(emptyLabel ?? '');
      } else if (searchValue === emptyLabel) {
        setSearchValue('');
      }
    }
  }, [isFocused, value]);

  useEffect(() => {
    setHighlightedIndex((options.length) ? 0 : -1);
  }, [options]);

  const selectOption = (option: TOption | null) => {
    setSearchValue(option?.label ?? emptyLabel ?? '');
    onChange(option?.value ?? null, option);
    // Wait for consumers to modify search value if they are going to.
    setTimeout(() => {
      setIsOpen(false);
    }, 0);
  };

  let boldOptions = options.map((option): AutocompleteBoldedOption<TOption> => {
    const { label } = option;
    const match = new RegExp(
      // Escape special characters like '*' that breaks the search.
      searchValue.replace(/([^\s\w])/g, '\\$1'),
      'i'
    ).exec(label)!;
    const startIndexForBoldText = !match ? 0 : match.index;
    const endIndexForBoldText = !match ? 0 : match.index + match[0].length;
    const firstSubstring = label.substring(0, startIndexForBoldText);
    const boldSubstring = label.substring(startIndexForBoldText, endIndexForBoldText);
    const lastSubstring = label.substring(endIndexForBoldText);
    return {
      option,
      firstSubstring,
      boldSubstring,
      lastSubstring
    };
  }).sort((a, b) =>
    // Sort options to display the ones with the least characters before the search first.
    (!searchValue) ? 0 : a.firstSubstring.length - b.firstSubstring.length
  );

  if (boldOptions.length) {
    const isSearchSelected = boldOptions.some(({ option }) =>
      option!.value === value
      && option!.label === searchValue
    );

    if (emptyLabel && (!searchValue || isSearchSelected)) {
      boldOptions = [
        {
          boldSubstring: '',
          firstSubstring: emptyLabel,
          option: null,
          lastSubstring: ''
        },
        ...boldOptions
      ];
    }
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || isLoading) {
      return;
    }

    const optionCount = boldOptions.length;
    if (optionCount === 0) {
      return;
    } else if (e.key === 'ArrowUp' || (e.ctrlKey && e.key === 'p')) {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev <= 0 ? optionCount - 1 : prev - 1
      );
    } else if (e.key === 'ArrowDown' || (e.ctrlKey && e.key === 'n')) {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev >= optionCount - 1 ? 0 : prev + 1
      );
    } else if (e.key === 'Enter' || (e.ctrlKey && e.key === 'y')) {
      e.preventDefault();
      selectOption(boldOptions[highlightedIndex].option);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  let message: string | null = null;
  if (isLoading) {
    message = labels?.isLoading ?? 'Loading results, please wait...';
  } else if (!boldOptions.length) {
    message = labels?.notFound ?? 'The item you are looking for does not exist.';
  }

  const content = (message) ? <p className={styles.message}>{message}</p> : (
    <div className={styles.list}>
      {boldOptions.map(({
        firstSubstring,
        boldSubstring,
        lastSubstring,
        option
      }, index) => {
        const {
          icon,
          label = emptyLabel ?? ''
        } = option ?? {};
        const isSelected = option?.value == value;
        const isHighlighted = index === highlightedIndex;
        return (
          <button
            className={classSet([
              isSelected && 'selected',
              isHighlighted && styles.highlighted
            ])}
            key={option?.id ?? label}
            onMouseEnter={() => setHighlightedIndex(index)}
            onMouseDown={() => selectOption(option)}
            title={label}
            type="button"
          >
            {icon && (typeof icon === 'string' ? <i className={`fe icon-${icon}`}></i> : <>{icon}</>)}
            <span>{firstSubstring}<strong>{boldSubstring}</strong>{lastSubstring}</span>
            <i className="fe icon-chevron-right" />
          </button>
        );
      })}
    </div>
  );

  const boxStyle = {
    left: (bbox?.left ?? 0) + 'px',
    top: (bbox?.bottom ?? 0) + 'px',
    width: style?.dropdown?.width ?? (bbox?.width ?? 0) + 'px'
  };
  const useHeader = !!labels?.title;

  return (
    <div ref={containerRef}>
      <InputIcon
        {...inputProps}
        // Prevent Chrome from trying to autofill values if the input has a name like "firstName".
        // This should be set to "off", but https://issues.chromium.org/issues/41163264#comment165 ignores the standard...
        autoComplete="off new-chrome-bug"
        disabled={disabled}
        onBlur={(event) => {
          setIsFocused(false);
          setIsOpen(false);
          onBlur?.(event);
        }}
        onKeyDown={onKeyDown}
        onChange={(event: SyntheticEvent<HTMLInputElement>) => {
          const newSearchValue = event.currentTarget.value;
          setSearchValue(newSearchValue);
          if (emptyLabel != null && value) {
            onChange(null, null);
          }
        }}
        onClick={(event: SyntheticEvent<HTMLInputElement>) => {
          event.currentTarget?.select?.();
        }}
        onFocus={(event) => {
          setIsFocused(true);
          event.target.select();
          onFocus?.(event);
        }}
        value={searchValue}
      />
      {isOpen && !disabled && (
        <Portal>
          <Box
            className={classSet([styles.autocompleteBox, !useHeader && styles.noHeader])}
            theme="noPadding"
            style={boxStyle}
          >
            {useHeader && (
              <div className={styles.header}>
                <p className={styles.subtitle}>{labels.title}</p>
                <i className="fe icon-x" />
              </div>
            )}
            {content}
          </Box>
        </Portal>
      )}
    </div>
  );
}

export type AutocompleteProps<TValue, TOption extends Option<TValue>> = (
  AutocompleteControlProps<TValue, TOption>
  & FormFieldControlProps
);

export function Autocomplete<TValue, TOption extends Option<TValue> = Option<TValue>>(
  props: AutocompleteProps<TValue, TOption>
): ReturnType<FC> {
  return (
    <ControlWithFormField control={AutocompleteControl<TValue, TOption>} {...props} />
  );
}
